import * as React from "react";
import { HelpCircle, Info, AlertCircle, CheckCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";

/**
 * TooltipHelp - Visible icon with contextual help
 * 
 * Use when users need to KNOW help is available:
 * - Form field explanations
 * - Feature descriptions  
 * - Complex UI elements
 * 
 * @see docs/UI_TOOLTIP_GUIDELINES.md for usage guide
 */
export interface TooltipHelpProps {
  content: React.ReactNode;
  /** Only "info" (ℹ️ blue) or "help" (? gray) allowed. Use Alerts for warnings/success. */
  type?: "info" | "help";
  size?: "sm" | "md" | "lg";
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
  triggerClassName?: string;
}

export function TooltipHelp({ 
  content, 
  type = "help", 
  size = "md",
  side = "top",
  className,
  triggerClassName 
}: TooltipHelpProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Detect mobile for touch-friendly behavior
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const iconMap = {
    info: Info,
    help: HelpCircle,
  };
  
  const colorMap = {
    info: "text-[var(--site-primary,#0f766e)] hover:text-[var(--site-primary,#0f766e)]/80",
    help: "text-muted-foreground hover:text-foreground",
  };
  
  const sizeMap = {
    sm: "h-3 w-3",
    md: "h-4 w-4", 
    lg: "h-5 w-5",
  };

  const Icon = iconMap[type];

  return (
    <Tooltip 
      delayDuration={isMobile ? 0 : 300}
      open={isMobile ? isOpen : undefined}
      onOpenChange={isMobile ? setIsOpen : undefined}
    >
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isMobile) {
              setIsOpen(!isOpen);
            }
          }}
          className={cn(
            "inline-flex items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "hover:bg-accent/50",
            // Larger tap target on mobile
            isMobile ? "p-2 min-w-[44px] min-h-[44px]" : "p-1",
            "touch-manipulation",
            triggerClassName
          )}
          aria-label={`${type} information`}
        >
          <Icon 
            className={cn(
              sizeMap[size],
              colorMap[type]
            )} 
          />
        </button>
      </TooltipTrigger>
      <TooltipContent 
        side={side} 
        className={cn(
          "max-w-xs leading-relaxed z-[70]",
          // Mobile: larger text, more padding
          isMobile ? "text-sm p-4" : "text-sm",
          className
        )}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

// Contextual help for forms
export interface FormHelpProps {
  label: string;
  description?: string;
  helpText?: React.ReactNode;
  required?: boolean;
  className?: string;
}

export function FormHelp({ 
  label, 
  description, 
  helpText, 
  required = false, 
  className 
}: FormHelpProps) {
  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
        {helpText && (
          <TooltipHelp content={helpText} type="help" size="sm" />
        )}
      </div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
}

// Feature callout with help
export interface FeatureCalloutProps {
  title: string;
  description: string;
  helpText?: React.ReactNode;
  type?: "info";
  children?: React.ReactNode;
  className?: string;
}

export function FeatureCallout({ 
  title, 
  description, 
  helpText, 
  type = "info", 
  children,
  className 
}: FeatureCalloutProps) {
  const bgColorMap = {
    info: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
  };

  return (
    <div className={cn(
      "rounded-lg border p-4",
      bgColorMap[type],
      className
    )}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-sm">{title}</h4>
        {helpText && (
          <TooltipHelp content={helpText} type="info" size="sm" />
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-3">{description}</p>
      {children}
    </div>
  );
}

// Upload-specific tooltips
export interface FileFormatTooltipProps {
  formats: string[];
  maxSize?: string;
}

export function FileFormatTooltip({ formats, maxSize = '100MB' }: FileFormatTooltipProps) {
  return (
    <TooltipHelp
      type="info"
      content={
        <div className="space-y-2">
          <p className="font-semibold">Accepted Formats:</p>
          <ul className="list-disc list-inside space-y-1">
            {formats.map((format) => (
              <li key={format} className="text-xs">
                {format}
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground pt-2 border-t">
            Maximum file size: {maxSize}
          </p>
        </div>
      }
    />
  );
}

export interface SizeLimitTooltipProps {
  currentSize: number;
  maxSize: number;
}

export function SizeLimitTooltip({ currentSize, maxSize }: SizeLimitTooltipProps) {
  const percentage = (currentSize / maxSize) * 100;
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <TooltipHelp
      type="info"
      content={
        <div className="space-y-2">
          <div className="flex justify-between text-xs">
            <span>Current: {formatBytes(currentSize)}</span>
            <span>Max: {formatBytes(maxSize)}</span>
          </div>
          <div className="w-full bg-secondary rounded-full h-2">
            <div
              className={cn(
                'h-2 rounded-full transition-all',
                percentage > 90 ? 'bg-destructive' : percentage > 70 ? 'bg-yellow-500' : 'bg-green-500'
              )}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {percentage > 100
              ? 'File exceeds size limit'
              : `${percentage.toFixed(0)}% of maximum size`}
          </p>
        </div>
      }
    />
  );
}

export interface DocumentTypeTooltipProps {
  documentType: string;
}

export function DocumentTypeTooltip({ documentType }: DocumentTypeTooltipProps) {
  const descriptions: Record<string, string> = {
    medical_history: 'Complete record of your past medical conditions, treatments, and surgeries',
    diagnosis: 'Official diagnosis report from your healthcare provider',
    lab_result: 'Results from laboratory tests including blood work and analysis',
    blood_test: 'Specific blood test results showing various health markers',
    biomarker: 'Test results showing biological markers related to specific conditions',
    genetic_test: 'Results from genetic testing for hereditary conditions',
    imaging: 'Medical imaging reports like X-rays, MRIs, or CT scans',
    prescription: 'Current or past medication prescriptions and dosage information',
    treatment_history: 'Record of all treatments and therapies received',
    allergy: 'Documentation of known allergies and adverse reactions',
    insurance: 'Insurance coverage details and identification',
    consent: 'Signed consent forms for medical procedures or trials',
  };

  return (
    <TooltipHelp
      type="info"
      content={
        <div className="space-y-1">
          <p className="font-semibold text-xs">About this document type:</p>
          <p className="text-xs">{descriptions[documentType] || 'Medical document for trial participation'}</p>
        </div>
      }
    />
  );
}